import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { MenuHeading } from "@/components/MenuHeading";
import { TradeListOffersBlock } from "@/components/trade/TradeListOffersBlock";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { ProLock } from "@/components/ProLock";
import { AppShell } from "@/components/AppShell";
import { DotRail } from "@/components/DotRail";
import { SpineDot } from "@/components/SectionHeadingDot";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Copy, Share2, Sparkles, X, Plus, RotateCcw, Save, HelpCircle, Check, ArrowRight, Users, Star,
} from "lucide-react";
import { listDexEntriesForVariant, DEX_STATE_EVENT } from "@/lib/dexStateStore";
import { computeDexGridNumbers } from "@/lib/dexGridFilter";
import { isBaseStage } from "@/lib/generated/baseStageSpecies.generated";
import { isBabyEvolution } from "@/lib/evolutionFamily";
import { isLegendaryDex } from "@/lib/legendaryDexNumbers";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import {
  FILTERS, defaultConfig, buildString, FILTER_META, mergeBuiltinRawFilters,
  type TradeStringConfig, type TriState,
} from "@/lib/tradeString";
import {
  listTradePresets, getDefaultTradePreset, getDefaultTradePresetId,
  saveTradePreset, deleteTradePreset, setDefaultTradePreset,
  TRADE_PRESET_EVENT, type SavedTradePreset,
} from "@/lib/tradePresetStore";
import { YOUR_INVITE_URL } from "@/lib/friends";

import { FEATURE_TEAM_BUILDER } from "@/lib/featureFlags";

type SourceKey =
  | "main" | "shiny" | "lucky" | "perfect" | "xxl" | "xxs"
  | "purified" | "legendary" | "transfer";

// Per-dex presentation used by the Step 1 visual picker. Each dex the user can
// complete gets an emoji + a one-word tagline so the "which dex" choice reads
// as a set of destinations rather than an opaque dropdown list.
const SOURCE_META: Record<SourceKey, { label: string; emoji: string; tag: string }> = {
  main:      { label: "Main Dex",      emoji: "🔴", tag: "Living dex" },
  shiny:     { label: "Shiny Dex",     emoji: "✨", tag: "Shinies" },
  lucky:     { label: "Lucky Dex",     emoji: "🍀", tag: "Luckies" },
  perfect:   { label: "Perfect Dex",   emoji: "💯", tag: "Hundos" },
  xxl:       { label: "XXL Dex",       emoji: "📏", tag: "Big size" },
  xxs:       { label: "XXS Dex",       emoji: "🤏", tag: "Small size" },
  purified:  { label: "Purified Dex",  emoji: "⚪", tag: "Purified" },
  legendary: { label: "Legendary Dex", emoji: "👑", tag: "Legendaries" },
  transfer:  { label: "Transfer search", emoji: "🗑️", tag: "No dex numbers" },
};
// The 2-column grid of completion dexes. "transfer" is rendered separately as a
// full-width, red-bordered card because it is a different kind of choice.
const SOURCE_ORDER: SourceKey[] = ["main", "shiny", "lucky", "perfect", "xxl", "xxs", "purified", "legendary"];

// Shared segmented-control option sets. Tri-state (Exclude / Include / Only)
// for traits + tags; bi-state (Exclude / Include) for custom operators.
const TRI_OPTIONS = [
  { value: "exclude", label: "Exclude" },
  { value: "include", label: "Include" },
  { value: "only", label: "Only" },
];
const TRI_NO_ONLY = TRI_OPTIONS.slice(0, 2);
// Custom operators are 3-state: Off (dormant) / Exclude / Include.
const RAW_OPTIONS = [
  { value: "off", label: "Off" },
  { value: "exclude", label: "Exclude" },
  { value: "include", label: "Include" },
];

/* ─── Main Trade page (v2 reskin) ─── */
export default function Trade() {
  const [, setLocation] = useLocation();

  return (
    <AppShell pageSpacing>
      <DotRail>
      <div className="pt-12 page-hero-top">
        {/* ── Hero header ── */}
        <MenuHeading
          eyebrow="Search"
          title="Your search system."
          greenSuffix="Simplified."
          subtitle="Turn the gaps in your Pokédex into a search string your friends can use to complete it."
          data-dotrail-skip
        />
      </div>

      <div className="space-y-4" data-dotrail-group>
        {/* The trade list (2026-09-29 owner report): the Pokémon marked on
            My Pokémon, so the page called Trade shows what is up for trade.
            Nothing while the list is empty. */}
        <div data-dotrail-dot>
          <TradeListOffersBlock />
        </div>

        {FEATURE_TEAM_BUILDER && (
        <button
          onClick={() => setLocation("/inventory")}
          data-testid="trade-best-six-entry"
          className="w-full text-left rounded-2xl p-3.5 hover-elevate"
          style={{
            background: "linear-gradient(135deg, hsl(142 78% 28% / 0.4), var(--surface-elev-1) 75%)",
            border: "1px solid hsl(142 78% 56% / 0.4)",
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(135deg, #00E676, #00B248)", color: "white" }}
            >
              <span className="text-base font-bold">6</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-bold leading-tight text-ink-strong">Best Six • Battle</p>
              <p className="text-[11px] text-ink-faint">
                Lock 6 for each league. Challenge a friend. Auto stat compare and play-by-play battle.
              </p>
            </div>
          </div>
        </button>
        )}

        {/* The whole search-string builder — its own "how it works" hero and
            the numbered Step 1 → 2 → 3 journey. */}
        <SearchStringTab />

      </div>
      </DotRail>
    </AppShell>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Search-string builder — guided Step 1 → 2 → 3 journey
   ═══════════════════════════════════════════════════════════════════ */

function SearchStringTab({ initialSource, onClear }: { initialSource?: SourceKey; onClear?: () => void }) {
  const { toast } = useToast();
  // Search is a Pro surface, gated tease-then-gate: Step 1 (pick a dex) and
  // Step 2 (filters) are free for everyone so the value is visible before the
  // ask; Step 3's output string is the payoff and is what Pro actually buys.
  // The TabBar only signposts this with a green PRO pill, so this is the real
  // gate and it must hold for deep links into /trade.
  const { tier } = useSession();
  const isPro = hasProAccess(tier);
  const startSource: SourceKey = initialSource ?? "lucky";
  const [source, setSource] = useState<SourceKey>(startSource);
  // Base-stage-only: a friend hands you the base species and you evolve it
  // yourself, so the string only asks for base-stage mons. Available for every
  // completion dex (hidden for legendary/transfer). On by default.
  const [baseStageOnly, setBaseStageOnly] = useState(true);

  // Initial config: the user's saved default preset if they have one, else the
  // built-in sensible defaults. Dex numbers are always auto from the selected dex.
  const [config, setConfig] = useState<TradeStringConfig>(() => {
    const ids = buildAutoIds(startSource, true);
    const def = getDefaultTradePreset();
    const base = def
      ? { ...def.config, source: startSource, dexNumbers: ids, dexMode: "auto" as const }
      : defaultConfig(ids, startSource);
    // Always fold in the permanent built-in operators (also migrates legacy
    // saved presets that predate them).
    return { ...base, rawFilters: mergeBuiltinRawFilters(base.rawFilters ?? []) };
  });

  const [savedPresets, setSavedPresets] = useState<SavedTradePreset[]>(() => listTradePresets());
  const [defaultPresetId, setDefaultPresetId] = useState<string | null>(() => getDefaultTradePresetId());
  // Which saved preset the current filters were loaded from, for highlighting.
  // Cleared to null the moment the user hand-edits any control.
  const [loadedPresetId, setLoadedPresetId] = useState<string | null>(() => getDefaultTradePresetId());

  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [savePresetName, setSavePresetName] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [rawInput, setRawInput] = useState("");

  function syncFromStore() {
    setSavedPresets(listTradePresets());
    setDefaultPresetId(getDefaultTradePresetId());
  }

  // Cross-tab / cross-mount sync of the preset list.
  useEffect(() => {
    window.addEventListener(TRADE_PRESET_EVENT, syncFromStore);
    return () => window.removeEventListener(TRADE_PRESET_EVENT, syncFromStore);
  }, []);

  // When the dex (or base-stage toggle) changes, regenerate the auto dex list.
  useEffect(() => {
    const ids = buildAutoIds(source, baseStageOnly);
    setConfig((c) => ({ ...c, source, dexNumbers: ids }));
  }, [source, baseStageOnly]);

  // Keep the auto dex list linked to the live scanned Pokédex.
  useEffect(() => {
    function refresh() {
      const ids = buildAutoIds(source, baseStageOnly);
      setConfig((c) => ({ ...c, dexNumbers: ids }));
    }
    window.addEventListener(DEX_STATE_EVENT, refresh);
    return () => window.removeEventListener(DEX_STATE_EVENT, refresh);
  }, [source, baseStageOnly]);

  // Rebuild string live
  const result = useMemo(() => buildString(config), [config]);
  const sourceLabel = SOURCE_META[config.source].label;

  // Any hand-edit diverges from the loaded preset.
  function markEdited() {
    setLoadedPresetId(null);
  }

  function setFilter(key: string, state: TriState) {
    setConfig((c) => ({ ...c, filters: { ...c.filters, [key]: state } }));
    markEdited();
  }
  function setTagState(idx: number, state: TriState) {
    setConfig((c) => ({ ...c, tags: c.tags.map((t, i) => (i === idx ? { ...t, state } : t)) }));
    markEdited();
  }
  function removeTag(idx: number) {
    setConfig((c) => ({ ...c, tags: c.tags.filter((_, i) => i !== idx) }));
    markEdited();
  }
  function addTag() {
    const raw = tagInput.trim();
    if (!raw) return;
    const tag = raw.startsWith("#") ? raw : `#${raw}`;
    if (config.tags.find((t) => t.tag.toLowerCase() === tag.toLowerCase())) {
      toast({ title: "Tag already added", description: tag });
      return;
    }
    setConfig((c) => ({ ...c, tags: [...c.tags, { tag, state: "exclude" }] }));
    setTagInput("");
    markEdited();
  }

  function setRawState(idx: number, state: "off" | "exclude" | "include") {
    setConfig((c) => ({ ...c, rawFilters: c.rawFilters.map((r, i) => (i === idx ? { ...r, state } : r)) }));
    markEdited();
  }
  function removeRaw(idx: number) {
    // Permanent built-in operators can't be removed.
    setConfig((c) => ({ ...c, rawFilters: c.rawFilters.filter((r, i) => i !== idx || r.permanent) }));
    markEdited();
  }
  function addRaw() {
    const expr = rawInput.trim().replace(/^[!&]+/, "");
    if (!expr) return;
    if (config.rawFilters.find((r) => r.expr.toLowerCase() === expr.toLowerCase())) {
      toast({ title: "Filter already added", description: expr });
      return;
    }
    setConfig((c) => ({ ...c, rawFilters: [...c.rawFilters, { expr, state: "include", permanent: false }] }));
    setRawInput("");
    markEdited();
  }

  function loadPreset(p: SavedTradePreset) {
    setConfig((c) => ({
      ...p.config,
      source: c.source,
      dexNumbers: c.dexNumbers,
      dexMode: "auto",
      rawFilters: mergeBuiltinRawFilters(p.config.rawFilters ?? []),
    }));
    setLoadedPresetId(p.id);
    // Loading a preset makes it the one the page opens with next time.
    setDefaultTradePreset(p.id);
    syncFromStore();
    toast({ title: "Preset applied", description: `"${p.name}" is now your default.` });
  }

  function deletePreset(id: string) {
    deleteTradePreset(id);
    if (loadedPresetId === id) setLoadedPresetId(null);
    syncFromStore();
  }

  function resetDefaults() {
    setConfig(defaultConfig(buildAutoIds(source, baseStageOnly), source));
    setLoadedPresetId(null);
    onClear?.();
    toast({ title: "Filters reset" });
  }

  function saveAsPreset() {
    const name = savePresetName.trim();
    if (!name) return;
    const preset = saveTradePreset(name, config);
    syncFromStore();
    setLoadedPresetId(preset.id);
    setSavePresetOpen(false);
    setSavePresetName("");
    toast({ title: "Preset saved", description: `"${preset.name}" will load by default next time.` });
  }

  function copyString() {
    navigator.clipboard.writeText(result.string).then(() => {
      toast({ title: "Copied to clipboard", description: `${result.string.length} characters` });
    }).catch(() => {
      toast({ title: "Copy failed", description: "Try long-pressing the text instead." });
    });
  }

  // Step 1 quick-copy: just the raw missing dex numbers for the selected dex,
  // with none of the Step 2 filters attached. Lets a player paste "1,4,7,…"
  // straight into the in-game search to see exactly which entries they still
  // need in that dex.
  function copyMissingNumbers() {
    const nums = config.dexNumbers;
    if (nums.length === 0) {
      toast({ title: "Nothing to copy", description: `Your ${sourceLabel} dex is already complete.` });
      return;
    }
    navigator.clipboard.writeText(nums.join(",")).then(() => {
      toast({ title: `Your missing ${sourceLabel} Pokémon search copied to clipboard` });
    }).catch(() => {
      toast({ title: "Copy failed", description: "Try again." });
    });
  }

  function shareString() {
    const text = `Get Nexus Dex to compare dexes with me, ${YOUR_INVITE_URL}\n\nMy missing-from-${config.source}-dex search string:\n${result.string}`;
    if (navigator.share) {
      navigator.share({ title: "Nexus Dex search string", text }).catch(() => { /* user canceled */ });
    } else {
      navigator.clipboard.writeText(text);
      toast({ title: "Copied share message", description: "Paste it into your messaging app." });
    }
  }

  return (
    <div className="space-y-4">
      {/* ── How it works: the completion loop, made visual ── */}
      <HowItWorks dexCount={result.dexCount} sourceLabel={sourceLabel} data-dotrail-dot />

      {/* ── Step 1 · Choose your Dex ── */}
      <StepCard
        n={1}
        title="Choose your Dex"
        subtitle="Which collection are you completing?"
        data-dotrail-dot
        headerAction={
          isPro && source !== "transfer" ? (
            <button
              type="button"
              data-testid="button-copy-missing-numbers"
              onClick={copyMissingNumbers}
              aria-label={`Copy your missing ${sourceLabel} dex numbers`}
              title="Copy just the missing dex numbers (no filters)"
              className="grid place-items-center w-8 h-8 rounded-full transition-colors"
              style={{
                background: "hsl(var(--primary) / 0.12)",
                color: "hsl(var(--primary))",
                border: "1px solid hsl(var(--primary) / 0.35)",
              }}
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          ) : undefined
        }
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {SOURCE_ORDER.map((key) => (
            <DexTypeCard
              key={key}
              active={source === key}
              emoji={SOURCE_META[key].emoji}
              label={SOURCE_META[key].label}
              tag={SOURCE_META[key].tag}
              onClick={() => setSource(key)}
              testid={`source-${key}`}
            />
          ))}
        </div>

        {/* Full-width, red-bordered special option: a transfer search carries no
            dex numbers, only the trait filters. */}
        <TransferDexCard active={source === "transfer"} onClick={() => setSource("transfer")} />

        {source === "transfer" ? (
          <div
            className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2.5"
            style={{ background: "hsl(0 84% 60% / 0.08)", border: "1px solid hsl(0 84% 60% / 0.30)" }}
            data-testid="text-dex-feeding"
          >
            <Sparkles className="w-4 h-4 shrink-0" style={{ color: "hsl(0 74% 55%)" }} />
            <p className="text-[12px] leading-snug text-muted-foreground">
              <span className="font-semibold text-foreground">No dex numbers.</span>{" "}
              The string is just your trait filters. It finds transfer candidates across your whole bag.
            </p>
          </div>
        ) : (
          <div
            className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2.5"
            style={{ background: "hsl(var(--primary) / 0.08)", border: "1px solid hsl(var(--primary) / 0.22)" }}
            data-testid="text-dex-feeding"
          >
            <Sparkles className="w-4 h-4 shrink-0" style={{ color: "hsl(var(--primary))" }} />
            <p className="text-[12px] leading-snug text-muted-foreground">
              <span className="font-mono font-semibold text-foreground">{config.dexNumbers.length.toLocaleString()}</span>{" "}
              missing {config.dexNumbers.length === 1 ? "entry" : "entries"} from your{" "}
              <span className="font-semibold text-foreground">{sourceLabel}</span> will be searched.
            </p>
          </div>
        )}

        {/* Base-stage filter — available for every completion dex, but not for
            the legendary or transfer sources. */}
        {source !== "legendary" && source !== "transfer" && (
          <label
            className="mt-2 flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 cursor-pointer"
            style={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }}
            data-testid="toggle-base-stage-only"
          >
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold">Base-stage only</span>
              <span className="block text-[11px] text-muted-foreground leading-snug">
                Skip 2nd/3rd-stage evolutions, ask for the base, evolve it yourself.
              </span>
            </span>
            <Switch
              checked={baseStageOnly}
              onCheckedChange={setBaseStageOnly}
              data-testid="switch-base-stage-only"
              className="shrink-0"
            />
          </label>
        )}
      </StepCard>

      {/* ── Step 2 · Filters to fine-tune (always expanded) ── */}
      <StepCard
        n={2}
        title="Filters to fine-tune"
        subtitle="Each trait adds a rule to your string, Exclude drops it, Only returns just that, Include ignores it."
        data-dotrail-dot
      >
        {/* Traits */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
          {FILTERS.map((f) => (
            <TuneRow
              key={f.key}
              label={f.label}
              options={f.allowOnly ? TRI_OPTIONS : TRI_NO_ONLY}
              value={config.filters[f.key] ?? "include"}
              onChange={(s) => setFilter(f.key, s as TriState)}
              testid={`filter-${f.key}`}
            />
          ))}
        </div>

        {/* Your tags — same control style, just extra rules the user adds. */}
        <div className="mt-5 pt-4 border-t border-border/50">
          <p className="text-sm font-semibold">Your tags</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            Hashtags you use in-game. Add your own and flip them Exclude / Include / Only, same as the traits above.
          </p>
          {config.tags.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
              {config.tags.map((t, idx) => (
                <TuneRow
                  key={idx}
                  label={t.tag}
                  mono
                  options={TRI_OPTIONS}
                  value={t.state}
                  onChange={(s) => setTagState(idx, s as TriState)}
                  onRemove={() => removeTag(idx)}
                  testid={`tag-${idx}`}
                />
              ))}
            </div>
          )}
          <AddRow
            value={tagInput}
            onChange={setTagInput}
            onAdd={addTag}
            placeholder="#mybestpokemon"
            mono
            testidInput="input-tag"
            testidBtn="button-add-tag"
          />
        </div>

        {/* Custom filters — any in-game operator, same control style. */}
        <div className="mt-5 pt-4 border-t border-border/50">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Custom filters</p>
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="button-show-examples" className="text-[11px] text-[hsl(var(--ai))] hover:underline inline-flex items-center gap-1">
                  <HelpCircle className="w-3 h-3" /> examples
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 text-xs space-y-1.5">
                <p className="font-semibold">Common operators</p>
                <div className="space-y-1 font-mono text-[11px] text-muted-foreground">
                  <div><span className="text-foreground">cp-1500</span>, CP 1500 or below</div>
                  <div><span className="text-foreground">distance100-</span>, caught 100km+ away (XL candy on trade)</div>
                  <div><span className="text-foreground">@water</span>, Water type</div>
                  <div><span className="text-foreground">evolve</span>, can evolve</div>
                  <div><span className="text-foreground">age0</span>, caught today</div>
                  <div><span className="text-foreground">2attack</span>, high attack IV</div>
                  <div><span className="text-foreground">4defense</span>, perfect defense IV</div>
                  <div><span className="text-foreground">+pikachu</span>, fuzzy match name</div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            Common operators are always here, leave one Off to ignore it, or flip it Exclude / Include.
            Add your own below.
          </p>
          {config.rawFilters.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
              {config.rawFilters.map((r, idx) => (
                <TuneRow
                  key={r.expr}
                  label={r.expr}
                  mono
                  options={RAW_OPTIONS}
                  value={r.state}
                  onChange={(s) => setRawState(idx, s as "off" | "exclude" | "include")}
                  onRemove={r.permanent ? undefined : () => removeRaw(idx)}
                  testid={`raw-${idx}`}
                />
              ))}
            </div>
          )}
          <AddRow
            value={rawInput}
            onChange={setRawInput}
            onAdd={addRaw}
            placeholder="custom"
            mono
            testidInput="input-raw"
            testidBtn="button-add-raw"
          />
        </div>

        {/* Save as preset + saved-preset list. */}
        <div className="mt-5 pt-4 border-t border-border/50">
          <div className="flex items-center gap-2">
            <Button
              data-testid="button-save-preset"
              onClick={() => setSavePresetOpen(true)}
              size="sm"
              className="rounded-full font-semibold"
              style={{
                background: "hsl(var(--primary) / 0.14)",
                color: "hsl(var(--primary))",
                border: "1px solid hsl(var(--primary) / 0.4)",
              }}
            >
              <Save className="w-3.5 h-3.5 mr-1.5" /> Save as preset
            </Button>
            <Button
              data-testid="button-reset-defaults"
              variant="ghost"
              size="sm"
              onClick={resetDefaults}
              className="btn-fit text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5 shrink-0" /> Reset
            </Button>
          </div>

          <SavedPresets
            presets={savedPresets}
            defaultId={defaultPresetId}
            loadedId={loadedPresetId}
            onLoad={loadPreset}
            onDelete={deletePreset}
          />
        </div>
      </StepCard>

      {/* ── Step 3 · Copy & share your string ── */}
      <StepCard
        n={3}
        title="Copy & share"
        subtitle="Send this string to a friend. They paste it into their in-game search."
        data-dotrail-dot
      >
        {/* Live active-filter mirror — what the string is acting on right now. */}
        <ActiveFilters config={config} />

        {/* Output — white in light mode, neon-on-dark in dark mode. */}
        <div className="trade-output-card mt-3 rounded-2xl p-5 relative overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Output string
            </p>
            <p className="text-[10px] font-mono text-muted-foreground">
              {result.string.length} characters
            </p>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            Excluding <span className="text-foreground font-mono">{result.excludeCount}</span> filters ·
            {" "}Only <span className="text-foreground font-mono">{result.onlyCount}</span> ·
            {" "}<span className="text-foreground font-mono">{result.dexCount}</span> dex numbers
          </p>

          {/* The string itself is the payoff. Non-Pro sees it built and blurred
              (proof it is real and tailored to their dex) but cannot read, select
              or copy it. aria-hidden + inert-by-pointer-events so a screen reader
              or a long-press cannot lift the text straight out of the tease. */}
          {isPro ? (
            <>
              <div>
                <SyntaxString value={result.string} />
              </div>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Button
                  data-testid="button-copy-string"
                  onClick={copyString}
                  className="flex-1 rounded-full font-semibold"
                  style={{ background: "linear-gradient(135deg, #00E676, #00B248)", color: "white" }}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy to Clipboard
                </Button>
                <Button
                  data-testid="button-share-string"
                  onClick={shareString}
                  variant="secondary"
                  className="flex-1 rounded-full font-semibold"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share with friend
                </Button>
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground/80 leading-relaxed">
                The search bar accepts long strings. If you hit a limit, Nexus Dex will offer to split into multiple copies.
              </p>
            </>
          ) : (
            <>
              {/* Non-Pro: lead with the unlock ask, THEN the blurred proof. The
                  string can run to thousands of characters; rendering the
                  ProLock after it stranded the CTA at the bottom of a tall blur,
                  easy to miss on scroll. The teased string stays as tailored
                  proof but is height-capped with a soft fade so it reads as a
                  preview rather than a wall. */}
              <div className="mb-3">
                <ProLock
                  feature="search-string"
                  source="trade-search-helper"
                  blurb="Your search string is ready. Go Pro to copy it, share it with friends, and build unlimited searches across every dex."
                />
              </div>
              <div
                className="blur-[6px] select-none pointer-events-none max-h-40 overflow-hidden"
                aria-hidden
                data-testid="trade-string-teased"
                style={{
                  maskImage: "linear-gradient(to bottom, black 55%, transparent 100%)",
                  WebkitMaskImage: "linear-gradient(to bottom, black 55%, transparent 100%)",
                }}
              >
                <SyntaxString value={result.string} />
              </div>
            </>
          )}
        </div>
      </StepCard>

      {/* Save preset dialog */}
      <Dialog open={savePresetOpen} onOpenChange={setSavePresetOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save preset</DialogTitle>
            <DialogDescription>Name your current filters. It'll appear below and load by default next time.</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="input-preset-name"
            value={savePresetName}
            onChange={(e) => setSavePresetName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveAsPreset(); } }}
            placeholder="e.g. My PvP-safe trade"
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSavePresetOpen(false)}>Cancel</Button>
            <Button data-testid="button-save-preset-confirm" onClick={saveAsPreset} disabled={!savePresetName.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── helpers used by Search String tab ─── */

function sourceVariant(s: SourceKey): PokedexVariant {
  if (s === "purified") return "purified";
  // main / legendary / transfer all read from the main ("pokemon") roster.
  if (s === "main" || s === "legendary" || s === "transfer") return "pokemon";
  return s; // shiny | lucky | perfect | xxl | xxs
}

function missingForVariant(variant: PokedexVariant): number[] {
  return computeDexGridNumbers({
    variant,
    recorded: listDexEntriesForVariant(variant),
    region: "all",
    status: "missing",
    search: "",
    sort: "dex-asc",
  });
}

function buildAutoIds(source: SourceKey, baseStageOnly = false): number[] {
  // Transfer search intentionally carries NO dex numbers — the string is just
  // the trait filters, so it matches transfer candidates across the whole bag.
  if (source === "transfer") return [];
  // Legendary Dex = the legendaries still missing from the main dex.
  if (source === "legendary") {
    return missingForVariant("pokemon").filter((id) => isLegendaryDex(id));
  }
  const ids = missingForVariant(sourceVariant(source));
  // Base-stage filter is available for every completion dex (a friend hands you
  // the base, you evolve it) — legendary/transfer are handled above and never
  // reach here.
  if (baseStageOnly) {
    // A baby's first evolution counts as base (Pikachu beside Pichu), the same
    // rule the Dex page's Base filter uses. See isBaseOrBabyEvolution.
    return ids.filter((id) => isBaseStage(id) || isBabyEvolution(id));
  }
  return ids;
}

/* ─── Journey chrome: numbered step card + how-it-works loop ─── */

function StepCard({
  n, title, subtitle, children, headerAction,
}: {
  n: number;
  title: string;
  subtitle: string;
  children: ReactNode;
  headerAction?: ReactNode;
  "data-dotrail-dot"?: boolean;
}) {
  return (
    <section className="surface-elev rounded-2xl p-4 relative" data-testid={`step-${n}`}>
      <SpineDot />
      <div className="flex items-start gap-3">
        <span
          className="mt-0.5 grid place-items-center w-7 h-7 rounded-full text-[13px] font-bold shrink-0"
          style={{
            background: "linear-gradient(135deg, #00E676, #00B248)",
            color: "white",
            boxShadow: "0 2px 8px hsl(142 78% 40% / 0.4)",
          }}
          aria-hidden
        >
          {n}
        </span>
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.18em]" style={{ color: "hsl(var(--primary))" }}>
            Step {n}
          </span>
          <h2 className="text-lg font-bold tracking-tight leading-tight">{title}</h2>
          <p className="mt-0.5 text-[12.5px] text-muted-foreground leading-snug">{subtitle}</p>
        </div>
        {headerAction && <div className="shrink-0">{headerAction}</div>}
      </div>
      <div className="mt-3.5">{children}</div>
    </section>
  );
}

// A selectable dex-type tile for Step 1.
function DexTypeCard({
  active, emoji, label, tag, onClick, testid,
}: {
  active: boolean; emoji: string; label: string; tag: string; onClick: () => void; testid?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={active}
      onClick={onClick}
      className="dex-neon-chip relative text-left rounded-xl px-3 py-2.5 transition-transform min-w-0"
      style={active ? {
        background: "linear-gradient(135deg, hsl(142 78% 40% / 0.22), hsl(142 78% 40% / 0.06))",
        border: "1.5px solid hsl(142 78% 52%)",
        boxShadow: "0 0 0 3px hsl(142 78% 52% / 0.12)",
      } : {
        background: "hsl(var(--card))",
        border: "1.5px solid hsl(var(--border))",
      }}
    >
      {active && (
        <span
          className="absolute top-1.5 right-1.5 grid place-items-center w-4 h-4 rounded-full"
          style={{ background: "hsl(142 78% 44%)", color: "white" }}
          aria-hidden
        >
          <Check className="w-2.5 h-2.5" strokeWidth={3} />
        </span>
      )}
      <span className="text-xl leading-none" aria-hidden>{emoji}</span>
      <span className="mt-1.5 block text-[13px] font-bold leading-tight truncate">{label}</span>
      <span className="block text-[10px] text-muted-foreground leading-tight truncate">{tag}</span>
    </button>
  );
}

// The odd-one-out Step 1 option: a full-width, red-bordered card. Selecting it
// strips all dex numbers from the string so it works as a transfer/cleanup
// search rather than a dex-completion search.
function TransferDexCard({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      data-testid="source-transfer"
      aria-pressed={active}
      onClick={onClick}
      className="dex-neon-chip relative w-full text-left rounded-xl px-3.5 py-2.5 mt-2 flex items-center min-h-[81px] transition-transform"
      style={active ? {
        background: "hsl(0 84% 60% / 0.12)",
        border: "1.5px solid hsl(0 84% 58%)",
        boxShadow: "0 0 0 3px hsl(0 84% 58% / 0.14)",
      } : {
        background: "hsl(0 84% 60% / 0.05)",
        border: "1.5px solid hsl(0 74% 55% / 0.6)",
      }}
    >
      {active && (
        <span
          className="absolute top-2 right-2 grid place-items-center w-4 h-4 rounded-full"
          style={{ background: "hsl(0 80% 55%)", color: "white" }}
          aria-hidden
        >
          <Check className="w-2.5 h-2.5" strokeWidth={3} />
        </span>
      )}
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xl leading-none shrink-0" aria-hidden>🗑️</span>
        <div className="min-w-0">
          <p className="text-[13px] font-bold leading-tight" style={{ color: "hsl(0 78% 56%)" }}>
            Don't include missing Dex
          </p>
          <p className="text-[11px] text-muted-foreground leading-tight">For Pokémon transfer searches</p>
        </div>
      </div>
    </button>
  );
}

function HowItWorks({
  dexCount, sourceLabel,
}: {
  dexCount: number; sourceLabel: string;
  "data-dotrail-dot"?: boolean;
}) {
  return (
    <section
      className="rounded-2xl p-4 relative"
      style={{
        background: "linear-gradient(135deg, hsl(var(--primary) / 0.10), hsl(var(--primary) / 0.02))",
        border: "1px solid hsl(var(--primary) / 0.28)",
      }}
      data-testid="how-it-works"
    >
      <SpineDot />
      <div className="flex items-center gap-1.5 mb-2.5">
        <Users className="w-3.5 h-3.5 shrink-0" style={{ color: "hsl(var(--primary))" }} />
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "hsl(var(--primary))" }}>
          How it works
        </span>
      </div>

      <p className="text-[13px] leading-relaxed text-foreground mb-3.5">
        This tool reads the gaps in your Dex and packs them into one search string.
        Share it, and a friend can scan <span className="font-semibold">their</span> bag for
        exactly the Pokémon that complete <span className="font-semibold">yours</span>.
      </p>

      <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] items-start gap-1 text-center">
        <FlowNode emoji="🙋" title="You" sub={`${dexCount.toLocaleString()} missing`} />
        <FlowArrow />
        <FlowNode emoji="🔎" title="Search string" sub="your dex #s" accent />
        <FlowArrow />
        <FlowNode emoji="🎒" title="Friend" sub="scans their bag" />
        <FlowArrow />
        <FlowNode emoji="✅" title="Your Dex" sub="completed" accent />
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
        Right now it's tracking your <span className="font-semibold text-foreground">{sourceLabel}</span>.
        Change it in Step 1 below.
      </p>
    </section>
  );
}

function FlowNode({ emoji, title, sub, accent }: { emoji: string; title: string; sub: string; accent?: boolean }) {
  return (
    <div className="flex flex-col items-center gap-0.5 min-w-0">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-base shrink-0"
        style={accent ? {
          background: "hsl(var(--primary) / 0.18)",
          border: "1px solid hsl(var(--primary) / 0.5)",
        } : {
          background: "hsl(var(--muted) / 0.7)",
          border: "1px solid hsl(var(--border))",
        }}
      >
        <span aria-hidden>{emoji}</span>
      </div>
      <span className="text-[10px] font-semibold text-foreground leading-tight text-balance">{title}</span>
      <span className="text-[9px] text-muted-foreground leading-tight text-balance">{sub}</span>
    </div>
  );
}

function FlowArrow() {
  return (
    <ArrowRight className="w-4 h-4 shrink-0 mt-3" style={{ color: "hsl(var(--primary) / 0.55)" }} aria-hidden />
  );
}

/* ─── Unified fine-tune controls: row + compact segmented toggle + add row ─── */

// One row: a label with an optional remove button, and a compact segmented
// Exclude/Include/Only control beneath it. Used identically by traits, tags,
// and custom operators so they all read as one system.
function TuneRow({
  label, mono, options, value, onChange, onRemove, testid,
}: {
  label: string;
  mono?: boolean;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  onRemove?: () => void;
  testid?: string;
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-1">
        <p className={`text-[12.5px] font-semibold truncate ${mono ? "font-mono" : ""}`}>{label}</p>
        {onRemove && (
          <button
            onClick={onRemove}
            aria-label="Remove"
            className="shrink-0 -mr-0.5 p-0.5 text-muted-foreground hover:text-destructive transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      <SegToggle options={options} value={value} onChange={onChange} testid={testid} />
    </div>
  );
}

// Compact segmented control — reduced height vs the old toggle. Active segment
// uses the theme --primary accent; inactive reads muted on a --card track.
function SegToggle({
  options, value, onChange, testid,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  testid?: string;
}) {
  return (
    <div
      role="group"
      data-testid={testid}
      className="mt-1 grid gap-0.5 rounded-md p-0.5"
      style={{
        gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
        background: "hsl(var(--card))",
        border: "1px solid hsl(var(--border))",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            data-testid={testid ? `${testid}-${o.value}` : undefined}
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={`rounded-[5px] py-[3px] text-[10px] font-semibold whitespace-nowrap transition ${active ? "" : "hover-elevate"}`}
            style={active ? {
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
            } : {
              background: "transparent",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Add-new input + button, shared by tags and custom filters.
function AddRow({
  value, onChange, onAdd, placeholder, mono, testidInput, testidBtn,
}: {
  value: string;
  onChange: (v: string) => void;
  onAdd: () => void;
  placeholder: string;
  mono?: boolean;
  testidInput?: string;
  testidBtn?: string;
}) {
  return (
    <div className="mt-3 flex gap-2">
      <Input
        data-testid={testidInput}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
        placeholder={placeholder}
        className={`h-9 rounded-lg text-sm ${mono ? "font-mono" : ""}`}
      />
      <Button data-testid={testidBtn} onClick={onAdd} variant="secondary" size="sm" className="rounded-lg h-9 shrink-0">
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
}

// Saved presets live below the Save button. Clicking one applies it and makes
// it the default that the page opens with. A star marks the current default.
function SavedPresets({
  presets, defaultId, loadedId, onLoad, onDelete,
}: {
  presets: SavedTradePreset[];
  defaultId: string | null;
  loadedId: string | null;
  onLoad: (p: SavedTradePreset) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="mt-4" data-testid="saved-presets">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">
        Your presets
      </p>
      {presets.length === 0 ? (
        <p className="text-[11px] text-muted-foreground leading-snug">
          No presets yet. Save your current filters and they'll appear here, your latest one loads
          automatically each time you open this page.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => {
            const active = loadedId === p.id;
            const isDefault = defaultId === p.id;
            return (
              <div
                key={p.id}
                data-testid={`preset-${p.id}`}
                className="inline-flex items-stretch rounded-full overflow-hidden"
                style={active ? {
                  background: "hsl(var(--primary))",
                  border: "1px solid hsl(var(--primary))",
                } : {
                  background: "transparent",
                  border: "1px solid var(--border-dark-segment)",
                }}
              >
                <button
                  onClick={() => onLoad(p)}
                  data-testid={`preset-load-${p.id}`}
                  className="pl-3 pr-2.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5"
                  style={{ color: active ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))" }}
                >
                  {isDefault && (
                    <Star
                      className="w-3 h-3 shrink-0"
                      style={{ fill: active ? "hsl(var(--primary-foreground))" : "hsl(var(--primary))", color: active ? "hsl(var(--primary-foreground))" : "hsl(var(--primary))" }}
                      aria-label="Default preset"
                    />
                  )}
                  {p.name}
                </button>
                <button
                  onClick={() => onDelete(p.id)}
                  data-testid={`preset-delete-${p.id}`}
                  aria-label={`Delete ${p.name}`}
                  className="pr-2.5 pl-1 flex items-center transition-colors hover:text-destructive"
                  style={{ color: active ? "hsl(var(--primary-foreground) / 0.7)" : "hsl(var(--muted-foreground))" }}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Components: active-filter mirror + syntax string ─── */

function ActiveFilters({ config }: { config: TradeStringConfig }) {
  // Every item that actually contributes a token to the string, colour-coded:
  // Exclude = green, Include = amber/yellow, Only = red border. Trait "include"
  // is the neutral default (no token) so it's skipped; tag/raw "include" DO emit
  // a token, so they show as INCL.
  type ChipState = "exclude" | "include" | "only";
  const chips: { key: string; label: string; emoji: string; state: ChipState }[] = [];
  for (const f of FILTERS) {
    const s = config.filters[f.key];
    if (s === "exclude" || s === "only") {
      const meta = FILTER_META[f.key] ?? { emoji: "•", short: f.label };
      chips.push({ key: f.key, label: meta.short, emoji: meta.emoji, state: s });
    }
  }
  for (const t of config.tags) {
    if (t.state === "exclude" || t.state === "only" || t.state === "include") {
      chips.push({ key: `tag-${t.tag}`, label: t.tag, emoji: "#️⃣", state: t.state });
    }
  }
  for (const r of config.rawFilters) {
    if (r.state === "exclude" || r.state === "include") {
      chips.push({ key: `raw-${r.expr}`, label: r.expr, emoji: "⚙️", state: r.state });
    }
  }

  const stateLabel: Record<ChipState, string> = { exclude: "excl", include: "incl", only: "only" };

  return (
    <div className="surface-elev rounded-xl p-4" data-testid="active-filters-mirror">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2.5">
        What this string filters
      </p>
      {chips.length === 0 ? (
        <p className="text-[11px] text-muted-foreground leading-snug">
          No filters active. The string returns everything for your missing dex numbers.
          Tune a filter in Step 2 and it lights up here.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {chips.map((c) => (
            <span
              key={c.key}
              data-testid={`active-chip-${c.key}`}
              className={`mchip mchip-${c.state} flex items-center gap-1.5 rounded-full pl-2.5 pr-2 py-1.5 text-[11px] font-semibold min-w-0`}
            >
              <span className="shrink-0">{c.emoji}</span>
              <span className="truncate">{c.label}</span>
              <span className="ml-auto shrink-0 text-[9px] uppercase tracking-wider opacity-70">
                {stateLabel[c.state]}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Syntax-highlighted output string. Token colours come from theme-aware
 * `.tok-*` classes (dark ink on the white light-mode card, neon on dark).
 */
function SyntaxString({ value }: { value: string }) {
  const tokens: { kind: "dex" | "amp" | "bang" | "tag" | "raw" | "name"; text: string }[] = [];
  if (!value) {
    return <pre className="font-mono text-[12px] leading-relaxed text-muted-foreground/50 select-all whitespace-pre-wrap break-all">(empty, no dex numbers and no filters)</pre>;
  }
  const parts = value.split("&");
  // A filter chunk ("!shiny", "#trade", "distance100-", "pikachu") tokenised with
  // its leading "!" split out so the bang always gets the tok-bang colour — even
  // when it's the very FIRST chunk (a transfer / no-dex-numbers string starts on
  // a filter, so the old "parts[0] is always dex numbers" assumption painted that
  // first "!" in the plain dex colour, out of step with the rest).
  const pushFilterChunk = (raw: string) => {
    let chunk = raw;
    if (chunk.startsWith("!")) {
      tokens.push({ kind: "bang", text: "!" });
      chunk = chunk.slice(1);
    }
    if (chunk.startsWith("#")) {
      tokens.push({ kind: "tag", text: chunk });
    } else if (/^[a-z*0-9]+$/i.test(chunk) && !chunk.includes("-") && !chunk.includes("@") && !chunk.includes("+")) {
      tokens.push({ kind: "name", text: chunk });
    } else {
      tokens.push({ kind: "raw", text: chunk });
    }
  };
  const dexPart = parts[0];
  // The leading chunk is the dex-number group only when it actually looks like
  // one ("1,2,5,6"); otherwise the string opened straight on a filter.
  const leadingIsDex = /^[0-9]+(,[0-9]+)*$/.test(dexPart);
  // Dex numbers render as "1,2,5,6" with NO spaces; the <wbr> in the render
  // below allows a wrap only BETWEEN numbers (never mid-digit, "95" -> "9"/"5").
  // The old approach put U+200B after each comma, which renders WITH width on
  // iOS and read as "1 , 2 , 5 , 6". Store the raw text; <wbr> does the wrapping.
  if (dexPart) {
    if (leadingIsDex) tokens.push({ kind: "dex", text: dexPart });
    else pushFilterChunk(dexPart);
  }
  for (let i = 1; i < parts.length; i++) {
    tokens.push({ kind: "amp", text: "&" });
    pushFilterChunk(parts[i]);
  }

  return (
    <pre
      className="font-mono text-[12px] leading-relaxed select-all whitespace-pre-wrap"
      style={{ wordBreak: "normal", overflowWrap: "break-word" }}
    >
      {tokens.map((t, i) => {
        const cls =
          t.kind === "amp"  ? "tok-amp" :
          t.kind === "bang" ? "tok-bang" :
          t.kind === "tag"  ? "tok-tag" :
          t.kind === "raw"  ? "tok-raw" :
          "text-foreground";
        if (t.kind === "dex") {
          const nums = t.text.split(",");
          return (
            <span key={i} className={cls}>
              {nums.map((n, j) => (
                <span key={j}>{j > 0 ? "," : ""}{j > 0 ? <wbr /> : null}{n}</span>
              ))}
            </span>
          );
        }
        if (t.kind === "amp") {
          return <span key={i} className={cls}>{t.text}<wbr /></span>;
        }
        return <span key={i} className={cls}>{t.text}</span>;
      })}
    </pre>
  );
}
